import { useEffect, useState } from 'react';
import { usePatchLoanEdit } from '@repo/api/endpoints/loan';
import type { LoanEditRequestBodySchema } from '@repo/api/endpoints/loan';
import type { MerchantResponse } from '@repo/api/endpoints/merchant';
import { LimitMerchant, Limit, Money, OfferLimit } from '@repo/shared/types';
import { useGetLoan } from '@repo/api/endpoints/loan';
import { loanProviderRequest, loanResponseStatus } from '@repo/shared/types';
import { env } from '@repo/env';

import { useLoanProcessModal } from '@/components/loan-process/hooks/use-loan-process-modal';
import { AgreementAndApprovals } from '@/components/loan-process/loan-stages/stage-2/agreement-and-approvals/agreement-and-approvals';
import { ApplicationSuspended } from '@/components/loan-process/loan-stages/shared/application-suspended/application-suspended';
import type { LoanStoreData } from '@/components/loan-process/store/loan-process.store';
import { useLoanProcessStore } from '@/components/loan-process/store/loan-process.store';
import {
  isLoanPositiveResponse,
  isLoanPositiveResponseStage1,
  isLoanPositiveResponseStage7,
} from '@/components/loan-process/loan-stages/shared/is-loan-positive-response';
import { ApplicationPositive } from '@/components/loan-process/loan-stages/stage-2/application-positive/application-positive';
import { ApplicationChangedLoanAmount } from '@/components/loan-process/loan-stages/stage-2/application-changed-loan-amount/application-changed-loan-amount';
import { useRefetchMerchantOnStageChange } from '@/components/loan-process/hooks/use-refetch-merchant-on-stage-change';

interface StageTwo {
  merchantResponseData: MerchantResponse | undefined;
  loanData: LoanStoreData | undefined;
  loanLimit: Limit | LimitMerchant | OfferLimit | undefined;
}

export interface HandleSubmitParams {
  formData: LoanEditRequestBodySchema;
  amount: { originalAmount?: Money; initialOriginalAmount?: Money };
}

export function StageTwo({ merchantResponseData, loanData, loanLimit }: StageTwo) {
  const [isGetLoanPollingEnabled, setIsGetLoanPollingEnabled] = useState(false);

  const {
    data: singleLoanData,
    error: singleLoanError,
    refetch: getSingleLoan,
    isFetching: isSingleLoanFetching,
  } = useGetLoan(isGetLoanPollingEnabled);

  const blockRefetch = loanData?.provider === 'liberis';
  useRefetchMerchantOnStageChange(blockRefetch);

  const { setLoanStage } = useLoanProcessModal();
  const {
    actions: { setLoanData, setLoanLimit },
    state: { viewOptions },
  } = useLoanProcessStore();

  const { mutate, status: editLoanStatus, error: editLoanError } = usePatchLoanEdit();
  const handleSubmitForm = ({ formData, amount }: HandleSubmitParams) => {
    if (amount.originalAmount !== amount.initialOriginalAmount) {
      mutate(formData, {
        onSuccess: () => {
          setIsGetLoanPollingEnabled(true);
        },
      });
    }
  };
  const handleAcceptTerms = () => {
    setLoanStage(3);
  };
  const isApplicationPositive =
    isLoanPositiveResponse(singleLoanData) &&
    singleLoanData.data.status === loanResponseStatus.enum.AGREED &&
    singleLoanData.data.stage === 2 &&
    singleLoanData.data.loan.providerRequest === loanProviderRequest.enum.gotEditNotification;

  const isApplicationFalsyPositive =
    isLoanPositiveResponse(singleLoanData) &&
    singleLoanData.data.status === loanResponseStatus.enum.AGREED &&
    singleLoanData.data.stage === 2 &&
    singleLoanData.data.loan.providerRequest !== loanProviderRequest.enum.gotNotification &&
    singleLoanData.data.loan.providerRequest !== loanProviderRequest.enum.editRequested;

  const isProviderRequestFailed =
    (isLoanPositiveResponse(singleLoanData) &&
      singleLoanData.data.loan.providerRequest === loanProviderRequest.enum.requestFailed) ||
    (isLoanPositiveResponseStage1(singleLoanData) &&
      singleLoanData.data.providerRequest === loanProviderRequest.enum.requestFailed) ||
    (isLoanPositiveResponseStage7(singleLoanData) &&
      singleLoanData.data.providerRequest === loanProviderRequest.enum.requestFailed);

  const isApplicationNegative =
    singleLoanError || singleLoanData?.data.status === 'REJECTED' || isProviderRequestFailed;

  const isLoanPoolingToSkip =
    isLoanPositiveResponse(singleLoanData) && singleLoanData.data.stage > 2;

  const isApplicationProcessing =
    editLoanStatus === 'pending' ||
    isSingleLoanFetching ||
    isGetLoanPollingEnabled ||
    (isLoanPositiveResponse(singleLoanData) &&
      singleLoanData.data.loan.providerRequest === loanProviderRequest.enum.editRequested);

  useEffect(() => {
    if (editLoanStatus === 'success' && loanData && merchantResponseData) {
      getSingleLoan({
        loanId: loanData.id.toString(),
        provider: loanData.provider,
        requestId: merchantResponseData.requestId,
      });
    }
  }, [editLoanStatus, loanData, merchantResponseData]);

  useEffect(() => {
    if (isApplicationPositive) {
      setLoanData({
        amount: singleLoanData.data.loan.amount,
        id: singleLoanData.data.loan.id,
        period: singleLoanData.data.loan.period,
        pricing: singleLoanData.data.loan.pricing,
        loanRepaymentPrice: singleLoanData.data.loan.loanRepaymentPrice,
        documents: singleLoanData.data.documents || [],
        loanAmountChange: singleLoanData.data.loan.loanAmountChange,
        provider: singleLoanData.data.loan.provider,
      });
      setLoanLimit(singleLoanData.data.limit);
      setIsGetLoanPollingEnabled(false);
    }
  }, [singleLoanData, isApplicationPositive]);

  useEffect(() => {
    if (
      isApplicationPositive ||
      isApplicationFalsyPositive ||
      isApplicationNegative ||
      isLoanPoolingToSkip
    ) {
      setIsGetLoanPollingEnabled(false);
    }
  }, [
    isApplicationFalsyPositive,
    isApplicationNegative,
    isApplicationPositive,
    isLoanPoolingToSkip,
  ]);

  const pollingTimeout = env.VITE_POLLING_TIMEOUT ? parseInt(env.VITE_POLLING_TIMEOUT, 10) : 600000;

  useEffect(() => {
    let pollingTimeoutTimer: NodeJS.Timeout | undefined;
    if (isGetLoanPollingEnabled) {
      pollingTimeoutTimer = setTimeout(() => {
        setIsGetLoanPollingEnabled(false);
      }, pollingTimeout);
    }

    return () => {
      clearTimeout(pollingTimeoutTimer);
    };
  }, [isGetLoanPollingEnabled]);

  useEffect(() => {
    return () => {
      setIsGetLoanPollingEnabled(false);
    };
  }, []);

  const savedLoans = merchantResponseData?.data?.loans;
  const lastSavedLoan = savedLoans?.[savedLoans.length - 1];

  if (
    loanData?.provider === 'liberis' &&
    lastSavedLoan &&
    lastSavedLoan.provider === 'liberis' &&
    lastSavedLoan.stage === 2
  ) {
    setLoanStage(3);

    return null;
  }

  if (loanLimit && viewOptions.stageViewType === 'APPLICATION_POSITIVE') {
    if (merchantResponseData && loanData && loanData.loanAmountChange) {
      return <ApplicationChangedLoanAmount loanData={loanData} loanLimit={loanLimit} />;
    }

    return <ApplicationPositive loanLimit={loanLimit} />;
  }

  if (merchantResponseData && loanLimit && loanData && !singleLoanError && !editLoanError) {
    return (
      <AgreementAndApprovals
        handleAcceptTerms={handleAcceptTerms}
        handleSubmitForm={handleSubmitForm}
        hasBeenEditRequestSent={editLoanStatus !== 'idle'}
        merchantResponseData={merchantResponseData}
        selectedProvider={loanData.provider}
        isApplicationProcessing={isApplicationProcessing}
        loanData={loanData}
        loanLimit={{
          ...loanLimit,
          used: loanLimit.used ?? 0,
        }}
      />
    );
  }
  if (editLoanError || singleLoanError || isApplicationFalsyPositive) {
    return <ApplicationSuspended />;
  }

  return <ApplicationSuspended />;
}
